import { Typography, Button, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GridColDef } from "@mui/x-data-grid";
import { Transaction } from "@appTypes/index";
import { useTransactionContext } from "@context/TransactionContext";
import TableView from "@components/Common/TableView";
import PurchaseCard from "@components/Purchases/PurchaseCard";
import formatDate from "@utils/formatDate";

const buildColumns = (t: (key: string) => string): GridColDef<Transaction>[] => [
  {
    field: "transactionDate",
    headerName: t("Date"),
    flex: 1,
    renderCell: ({ row }) =>
      formatDate(
        row.transactionDate,
        { month: "short", day: "numeric", year: "numeric" }
      ),
  },
  {
    field: "productName",
    headerName: t("Description"),
    flex: 2,
    renderCell: ({ row }) => row.productName,
  },
  {
    field: "amount",
    headerName: t("Amount"),
    flex: 1,
    align: "left",
    headerAlign: "left",
    renderCell: ({ row }) => `$${row.amount.toFixed(2)}`,
  },
];

const PurchaseContainer = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { displayedTransactions, areTransactionsLoading, error, isExpanded, toggleExpanded, hasMore } =
    useTransactionContext();

  if (areTransactionsLoading) {
    return <Typography>{t("Loading purchases")}</Typography>;
  }

  if (error) {
    return <Typography color="error">{t("Error loading purchases")}</Typography>;
  }

  const columns = buildColumns(t);

  return (
    <div>
      {isMobile ? (
        displayedTransactions.map((tx) => (
          <PurchaseCard key={`${tx.transactionDate}-${tx.amount}-${tx.productName}`} transaction={tx} />
        ))
      ) : (
        <TableView
          rows={displayedTransactions}
          columns={columns}
          tableViewType="purchases"
          noRowsMessage={t("No transactions found")}
          getRowId={(row) => `${row.transactionDate}-${row.amount}-${row.productName}`}
        />
      )}

      {hasMore && (
        <div className="text-center mt-4">
          <Button
            variant="text"
            size="small"
            color="primary"
            onClick={toggleExpanded}
          >
            {isExpanded ? t("Show less") : t("Show all transactions")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PurchaseContainer;
